<template>
  <div class="services-table">
    <!-- NEW SERVICE BUTTON AND SEARCH -->
    <b-row
      v-if="showSearch || showButton"
      class="mb-2 d-flex flex-wrap"
      :class="{
        'justify-content-between': showButton,
        'justify-content-start': !showButton,
      }"
    >
      <b-col v-if="showButton" cols="3" md="auto" class="mb-1 mb-md-0">
        <b-button
          variant="primary"
          block
          class="d-flex justify-content-center"
          @click.prevent="newService"
        >
          <feather-icon icon="PlusIcon" class="d-block d-md-none" />
          <span class="d-none d-md-inline">{{ $t("Afegeix servei") }}</span>
        </b-button>
      </b-col>
      <b-col
        v-if="showSearch"
        cols="9"
        md="auto"
        class="mb-1 mb-md-0"
        :class="{
          'justify-content-between': showButton,
          'justify-content-start': !showButton,
        }"
      >
        <b-form-input
          id="filterInput"
          v-model="filter"
          type="search"
          :placeholder="`${$t('Cerca')}...`"
          :disabled="loading"
        />
      </b-col>
    </b-row>

    <!-- SERVICES TABLE -->
    <b-row>
      <b-col md="12">
        <b-table
          striped
          borderless
          hover
          responsive
          class="position-relative"
          :busy="loading"
          :per-page="perPage"
          :current-page="currentPage"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          sort-icon-left
          :items="services"
          show-empty
          :empty-text="$t('errors.emptyServicesTable.title')"
          :empty-filtered-text="$t('errors.emptyServicesTable.title')"
          :fields="fields"
          :filter="filter"
          @filtered="onFiltered"
        >
          <template #table-busy>
            <div class="text-center text-primary my-2">
              <b-spinner class="align-middle mr-2" />
              <strong>{{ $t("Carregant") }}...</strong>
            </div>
          </template>

          <!-- Column: Actions -->
          <template #cell(actions)="data">
            <b-dropdown variant="link" no-caret>
              <template #button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="16"
                  class="align-middle text-body"
                />
              </template>

              <b-dropdown-item @click="onEditServiceClicked(data.item.uuid)">
                <feather-icon icon="EditIcon" />
                <span class="align-middle ml-50">Edita</span>
              </b-dropdown-item>

              <b-dropdown-item @click="confirmDeleteService(data.item.uuid)">
                <feather-icon icon="TrashIcon" />
                <span class="align-middle ml-50">Elimina</span>
              </b-dropdown-item>
            </b-dropdown>
          </template>
        </b-table>
      </b-col>
    </b-row>

    <!-- PAGINATION -->
    <b-row
      v-show="!loading"
      v-if="services.length > pageOptions[0]"
      class="d-flex justify-content-between align-items-center"
    >
      <b-col
        cols="12"
        md="4"
        lg="3"
        class="mb-1 mb-md-0 d-flex justify-content-center justify-content-md-start"
      >
        <b-form-group
          id="per-page-input"
          :label="`${$t('Per pàgina')}`"
          label-cols="6"
          label-align="left"
          label-size="sm"
          label-for="sortBySelect"
          class="text-nowrap mr-1"
        >
          <b-form-select
            id="perPageSelect"
            v-model="perPage"
            size="sm"
            inline
            :options="pageOptions"
          />
        </b-form-group>
      </b-col>
      <b-col
        cols="12"
        md="4"
        lg="3"
        class="mb-1 mb-md-0 d-flex justify-content-center justify-content-md-end"
      >
        <div>
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            first-number
            last-number
            prev-class="prev-item"
            next-class="next-item"
          >
            <template #prev-text>
              <feather-icon icon="ChevronLeftIcon" size="18" />
            </template>
            <template #next-text>
              <feather-icon icon="ChevronRightIcon" size="18" />
            </template>
          </b-pagination>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BTable,
  BRow,
  BCol,
  BFormGroup,
  BFormSelect,
  BPagination,
  BFormInput,
  BButton,
  BSpinner,
  BDropdown,
  BDropdownItem,
} from "bootstrap-vue";

export default {
  components: {
    BTable,
    BRow,
    BCol,
    BFormGroup,
    BFormSelect,
    BPagination,
    BFormInput,
    BButton,
    BSpinner,
    BDropdown,
    BDropdownItem,
  },
  props: {
    services: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: () => false,
    },
    showButton: {
      type: Boolean,
      default: () => false,
    },
    showSearch: {
      type: Boolean,
      default: () => false,
    },
  },
  data() {
    return {
      isBusy: false,
      sortBy: "name",
      sortDesc: false,
      perPage: 10,
      pageOptions: [5, 10, 25, 50],
      totalRows: 1,
      currentPage: 1,
      filter: null,
      fields: [
        { key: "name", label: this.$t("Nom"), sortable: true },
        { key: "mandatory", label: this.$t("Obligatori"), sortable: true },
        { key: "costPrice", label: this.$t("Cost"), sortable: true },
        { key: "price", label: this.$t("PVP"), sortable: true },
        {
          key: "paymentFrequency",
          label: this.$t("Freqüència pagament"),
          sortable: true,
        },
        {
          key: "paymentTime",
          label: this.$t("Moment pagament"),
          sortable: true,
        },
        {
          key: "paymentMethods",
          label: this.$t("Mètode pagament"),
          sortable: true,
        },
        {
          key: "amount",
          label: this.$t("Serveis disponibles"),
          sortable: true,
        },
        {
          key: "actions",
          label: this.$t("Accions"),
          thStyle: { width: "95px" },
        },
      ],
    };
  },
  watch: {
    services(services) {
      this.totalRows = services.length;
      this.currentPage = 1;
    },
  },
  mounted() {
    // Set the initial number of items
    this.totalRows = this.services.length;
  },
  methods: {
    onFiltered(filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },
    newService() {
      this.$emit("button-click");
    },
    onEditServiceClicked(serviceUuid) {
      this.$emit("edit-service-click", serviceUuid);
    },
    confirmDeleteService(serviceUuid) {
      this.$swal({
        title: "Eliminar servei",
        text: "Estàs a punt d'eliminar aquest servei, estàs segur/a?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Elimina",
        cancelButtonText: "Cancel·la",
        customClass: {
          confirmButton: "btn btn-danger",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) this.deleteService(serviceUuid);
      });
    },
    deleteService(serviceUuid) {
      this.$store.dispatch("app/setLoading", true);
      setTimeout(() => {
        this.$store
          .dispatch("accommodation/deleteService", serviceUuid)
          .then(() => this.$emit("service-deleted"))
          .catch(() => {
            this.$emit("delete-service-error");
            // TODO: Log error in Sentry? Only if is not logged in the store
          })
          .finally(() => this.$store.dispatch("app/setLoading", false));
      }, 100);
    },
  },
};
</script>

<style lang="scss">
.services-table {
  #per-page-input__BV_label_ {
    margin-top: 5px;
  }
}
</style>
